import React, { useState, useEffect, useContext } from 'react';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import EngagingProsLaborCard from './EngagingProsLaborCard/EngagingProsLaborCard';
import ResourceCard from '/src/Components/ResourceCard/ResourceCard';
import { ThumbtackStorage } from '/src/services/QGDStorage';
import ServiceVendorListings from '/src/Components/ServiceVendorListings/ServiceVendorListings';
import { MoveLeft } from 'lucide-react';
import { AddressSearchContext, WindowSizeContext } from '/src/App';

import './style.css';

const areObjectsEqual = (newObj, selectedObj) => {
    if (!newObj || !selectedObj) return false;
    return newObj.who_does_work === selectedObj.who_does_work &&
        newObj.projects_list.length === selectedObj.projects_list.length &&
        newObj.projects_list.every(project =>
            selectedObj.projects_list.some(p => p.project_id === project.project_id)
        );
};

const groupProjectsByWhoDoesWork = (projects, labor_types, resources) => {
    return projects.reduce((acc, project) => {
        const whoDoesWork = project.project_who_does_work;
        const whoDoesWorkId = project.project_who_does_work_id;

        // Check if we already have this worker type in our accumulator
        const existingGroup = acc.find(group => group.who_does_work === whoDoesWork);

        if (existingGroup) {
            // Add project to existing group
            existingGroup.projects_list.push(project);
        } else {
            // Get the labor type from the labor_types object using the whoDoesWorkId
            const laborType = labor_types[whoDoesWorkId] || {};
            const laborTypeResourcesIds = laborType.resources || [];

            // Get the resources for each of the labor type resources
            let laborTypeResourcesWithResources = [];
            if (laborTypeResourcesIds.length > 0) {
                laborTypeResourcesWithResources = laborTypeResourcesIds.map(resourceId => {
                    return resources.find(r => r.id === resourceId);
                }).filter(Boolean); // Remove any undefined results if a resource wasn't found
            }

            // Create new group with this project
            acc.push({
                who_does_work: whoDoesWork,
                who_does_work_id: whoDoesWorkId,
                who_does_work_icon: project.project_who_does_work_icon,
                projects_list: [project],
                labor_type_resources: laborTypeResourcesWithResources
            });
        }

        return acc;
    }, []);
};

const EngagingPros = ({
    propertyData,
    userPlanSelection,
    companies,
    projects
}) => {
    const { deviceType } = useContext(WindowSizeContext);
    const { portalData } = useContext(AddressSearchContext);
    const { labor_types, resources } = portalData;
    console.log('labor_types ', labor_types);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [allThumbtackListings, setAllThumbtackListings] = useState({});
    const [selectedGroupThumbtackListings, setSelectedGroupThumbtackListings] = useState([]);
    // Create the state for whether to show the service / request flow iframe
    const [showIFrame, setShowIFrame] = useState(false);
    const [iframeUrl, setIframeUrl] = useState('');
    const [service_page_iframe_id, setServicePageIframeId] = useState('');

    // The service networks
    const service_networks = [
        { name: "Angi", url: "https://www.angi.com/", icon: "angi.png" },
        { name: "Thumbtack", url: "https://www.thumbtack.com/", icon: "thumbtack.png" },
        { name: "HomeAdvisor", url: "https://www.homeadvisor.com/", icon: "homeadvisor.png" },
        { name: "Houzz", url: "https://www.houzz.com/", icon: "houzz.png" },
    ];

    // Get the zip code from the property data
    const zip_code = propertyData.data.property_details.zip;

    // Get all of the thumbtack listings for the zip code
    useEffect(() => {
        async function fetchThumbtackListings() {
            try {
                await ThumbtackStorage.init();
                const listings = await ThumbtackStorage.getListingsByZip(zip_code);
                // console.log('Thumbtack listings for zip:', listings);
                setAllThumbtackListings(listings);
            } catch (error) {
                console.error('Error fetching Thumbtack listings:', error);
            }
        }

        fetchThumbtackListings();
    }, [zip_code]);

    // useEffect that listens to selectedGroup and when that changes it takes the project_ids from the selectedGroup.projects_list and filters the allThumbtackListings to only include the listings with the matching project_ids
    useEffect(() => {
        if (selectedGroup) {
            const projectIds = selectedGroup.projects_list.map(project => project.project_id);

            // Collect all listings into a single array
            const combinedListings = projectIds.reduce((acc, projectId) => {
                if (allThumbtackListings[projectId]?.listings) {
                    return [...acc, ...allThumbtackListings[projectId].listings];
                }
                return acc;
            }, []);

            // Remove duplicates using Map with service_id as key
            const uniqueListingsMap = new Map();
            combinedListings.forEach(listing => {
                if (!uniqueListingsMap.has(listing.service_id)) {
                    uniqueListingsMap.set(listing.service_id, listing);
                }
            });

            // Convert back to array and sort by rating (high to low)
            const filteredListings = Array.from(uniqueListingsMap.values())
                .sort((a, b) => b.rating - a.rating);

            setSelectedGroupThumbtackListings(filteredListings);
        }
    }, [selectedGroup, allThumbtackListings]);

    console.log('allThumbtackListings', allThumbtackListings);
    console.log('selectedGroupThumbtackListings', selectedGroupThumbtackListings);
    // Load the resources from local storage
    // const resources = localStorage.getItem('resources');
    // const resourcesArray = JSON.parse(resources);

    // Get resource with id '88'
    const testResource = resources.find(resource => resource.id === '88');

    // Get the projects from the user plan selection
    const userPlanSelectionProjects = userPlanSelection.projectData || [];
    const totalProjects = userPlanSelectionProjects.length;

    // Get the projects from the userPlanSelectionProjects where diyPreference is 'pro'
    const proProjects = userPlanSelectionProjects.filter(project => project.diyPreference === 'pro');
    const diyProjects = userPlanSelectionProjects.filter(project => project.diyPreference === 'diy');

    // Process the diyProjects to get the full project data
    const diyProjectsIds = diyProjects.map(project => project.project_id);
    const fullDIYProjects = projects.filter(project => diyProjectsIds.includes(project.project_id));
    const groupedDIYProjects = groupProjectsByWhoDoesWork(fullDIYProjects, labor_types, resources);


    // console.log('diyProjects', diyProjects);
    // console.log('diyProjectsIds', diyProjectsIds);
    // console.log('fullDIYProjects', fullDIYProjects);
    // console.log('groupedDIYProjects', groupedDIYProjects);

    // Process the proProjects to get the full project data
    // Get the ids of the proProjects
    const proProjectIds = proProjects.map(project => project.project_id);
    // console.log('proProjectIds', proProjectIds);

    // useEffect that listens to a

    // Get the full project for each of the proProjects by filtering the projects array to projects with a project_id that matches a project_id in the proProjects array
    const fullProjects = projects.filter(project => proProjectIds.includes(project.project_id));

    // Group projects by who does the work
    const groupedProjects = groupProjectsByWhoDoesWork(fullProjects, labor_types, resources);
    // const groupedProjects = fullProjects.reduce((acc, project) => {
    //     const whoDoesWork = project.project_who_does_work;
    //     const whoDoesWorkId = project.project_who_does_work_id;

    //     // Check if we already have this worker type in our accumulator
    //     const existingGroup = acc.find(group => group.who_does_work === whoDoesWork);

    //     if (existingGroup) {
    //         // Add project to existing group
    //         existingGroup.projects_list.push(project);
    //     } else {

    //         // Get the labor type from the labor_types object using the whoDoesWorkId
    //         const laborType = labor_types[whoDoesWorkId] || {};
    //         const laborTypeResourcesIds = laborType.resources || [];

    //         // Get the resources for each of the labor type resources
    //         let laborTypeResourcesWithResources = [];
    //         if (laborTypeResourcesIds.length > 0) {
    //             laborTypeResourcesWithResources = laborTypeResourcesIds.map(resourceId => {
    //                 return resources.find(r => r.id === resourceId);
    //             }).filter(Boolean); // Remove any undefined results if a resource wasn't found
    //         }

    //         // Create new group with this project
    //         acc.push({
    //             who_does_work: whoDoesWork,
    //             who_does_work_id: whoDoesWorkId,
    //             who_does_work_icon: project.project_who_does_work_icon,
    //             projects_list: [project],
    //             labor_type_resources: laborTypeResourcesWithResources
    //         });
    //     }

    //     return acc;
    // }, []);

    // Handler for when a group is clicked (if the group is already selected, deselect it)
    const handleGroupClick = (group) => {
        if (selectedGroup && areObjectsEqual(selectedGroup, group)) {
            setSelectedGroup(null);
        } else {
            setSelectedGroup(group);
        }
    }


    // Create the intro sentence
    let introSentence = '';
    let countSentence = '';
    if (proProjects.length > 0) {

        if (proProjects.length === 1) {
            countSentence = "the project in this plan "
        } else if (proProjects.length === totalProjects) {
            countSentence = "all of the projects in this plan "
        } else {
            countSentence = `${proProjects.length} of the ${totalProjects} project${totalProjects === 1 ? '' : 's'} in this plan `
        }

        introSentence = `You'll be hiring a professional for ${countSentence}. Click on a service technician type below to see listings of professionals who can do the work as well as what you should be looking for.`;
    } else {
        introSentence = `It looks like this plan doesn't include any projects that you're looking to hire a professional for. Still, in case you change your mind, let's walk through the type of help you'd need and the things to keep in mind.`;
    }

    const handleIFrameClick = (url, id) => {
        setIframeUrl(url);
        setServicePageIframeId(id);
        setShowIFrame(true);
    }

    const handleIFrameClose = () => {
        setShowIFrame(false);
        setIframeUrl('');
        setServicePageIframeId('');
    }

    const category_pk = '283110145487618506'
    const utm_source = 'cma-quoll'
    const utm_campaign = 'cma-quoll'
    const iframe_id = 'quoll2'
    // const zip_code = '90068'
    const is_category_editable = false
    const is_zipcode_editable = false


    const iFrameURL = `https://staging-partner.thumbtack.com/embed/pro-list?category_pk=${category_pk}&utm_source=${utm_source}&utm_campaign=${utm_campaign}&iframe_id=${iframe_id}&zip_code=${zip_code}&is_category_editable=${is_category_editable}&is_zipcode_editable=${is_zipcode_editable}`

    console.log('groupedProjects', groupedProjects);
    return (
        <div className="taking-action-section-container">
            {!selectedGroup && (
                <>
                    <h1>Engaging Pros</h1>
                    <p className="engaging-pros-intro-sentence text-align-center" dangerouslySetInnerHTML={{ __html: introSentence }} />
                    <div className="engaging-pros-who-does-work-container">

                        {groupedProjects.length > 0 ? (
                            <>
                                {groupedProjects.map((group) => (
                                    <EngagingProsLaborCard
                                        key={group.who_does_work}
                                        group={group}
                                        handleGroupClick={handleGroupClick}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                {groupedDIYProjects.map((group) => (
                                    <EngagingProsLaborCard
                                        key={group.who_does_work}
                                        group={group}
                                        handleGroupClick={null}
                                        isSelected={true}
                                        showLaborDetails={true}
                                        showResources={true}
                                        canBeClicked={false}
                                    />
                                ))}
                            </>
                        )}

                    </div>
                    <span className="engaging-pros-select-technician-type-text italic-text text-align-center">Select a technician type to see guidance and listings</span>
                </>
            )}
            {selectedGroup && (
                <>
                    <div className="engaging-pros-selected-group-header-container">
                        <button className="engaging-pros-selected-group-back-button" onClick={() => setSelectedGroup(null)}>
                            <MoveLeft />
                            All Vendor Types
                        </button>
                        <div className="engaging-pros-selected-group-header-title-container">
                            <div className="engaging-pros-icon-container">
                                <img
                                    src={CreateCloudfrontUrl(selectedGroup.who_does_work_icon)}
                                    alt={`${selectedGroup.who_does_work} icon`}
                                    className="engaging-pros-icon"
                                />
                            </div>
                            <h1 className="engaging-pros-selected-group-header">
                                Hiring {/^[aeiou]/i.test(selectedGroup.who_does_work) ? 'an' : 'a'} {selectedGroup.who_does_work} {selectedGroup.who_does_work === 'contractor' ? 'for' : ''}
                            </h1>
                        </div>
                    </div>
                    <div className={`engaging-pros-selected-group-container ${deviceType}`}>
                        <div className={`engaging-pros-selected-group-left-container ${deviceType}`}>
                            <h3 className="engaging-pros-selected-group-right-container-header">Project{selectedGroup.projects_list.length > 1 ? 's' : ''} They'd Handle</h3>

                            <EngagingProsLaborCard
                                key={selectedGroup.who_does_work}
                                group={selectedGroup}
                                handleGroupClick={null}
                                isSelected={true}
                                showLaborDetails={false}
                            />
                        </div>
                        <div className={`engaging-pros-selected-group-right-container ${deviceType}`}>
                            <h3 className="engaging-pros-selected-group-right-container-header">What to Look For</h3>
                            <div className="engaging-pros-what-to-look-for-cards-container">
                                {selectedGroup.labor_type_resources.map(resource => (
                                    <ResourceCard
                                        key={resource.id}
                                        resource={resource}
                                        handleResourceClick={null}
                                        showTags={false}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={`engaging-pros-selected-group-vendor-listings ${deviceType}`}>
                        {!showIFrame && (
                            <>
                                <h3 className="engaging-pros-selected-group-right-container-header">Where to Find Them</h3>
                                <ServiceVendorListings
                                    thumbtackListings={selectedGroupThumbtackListings}
                                    handleIFrameClick={handleIFrameClick}
                                />
                            </>
                        )}
                        {showIFrame && (
                            <div className="thumbtack-listing-iframe-container">
                                <button onClick={handleIFrameClose} className="thumbtack-listing-iframe-close-btn">
                                    <MoveLeft />
                                    Back to All Listings
                                </button>
                                <iframe
                                    src={iframeUrl}
                                    className="thumbtack-listing-iframe"
                                    id={service_page_iframe_id}
                                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                    width="100%"
                                    height="100%"
                                // style={{ border: 'none' }}
                                />
                            </div>
                        )}

                    </div>
                </>
            )}
        </div>
    );
};

export default EngagingPros;